<template>
  <Card id="pd-performance">
    <template #header>
      <View justify="space-between" align="center" wrap="wrap" class="stats-title">
        <Tabs :model-value="activeTab" :tabs="statsTabs" @update:model-value="activeTab = $event as StatsTab" />

        <View
          gap="xs"
          align="center"
          class="controls"
          :class="{ 'controls-hidden': activeTab !== StatsTab.LEASING_FUNNEL }"
        >
          <IconButton
            icon="download-cloud-02"
            variant="ghost"
            size="medium"
            class="download"
            @click="handleDownloadStats"
          />
          <MenuButton :buttonLabel="selectedFilter" size="medium">
            <MenuButtonItem
              v-for="filter in timeFilters"
              :key="filter"
              :title="filter"
              @click="onFilterSelected(filter)"
            />
          </MenuButton>
        </View>
      </View>
    </template>

    <TabTransition :active-key="activeTab">
      <!-- No gap here: the preview fold carries its own top spacing inside its collapse
           animation, so the space appears together with the widget instead of before it. -->
      <View v-if="activeTab === StatsTab.LEASING_FUNNEL" direction="column">
        <StatsGrid>
          <StatGroup v-for="stat in statCards" :key="stat.key" :to="stat.to">
            <template #label>
              <Text variant="h5" color="secondary">{{ stat.title }}</Text>
            </template>
            <div class="stat-items">
              <View v-for="item in stat.items" :key="item.label" direction="column" gap="xs">
                <Text variant="h2" as="div">{{ formatCount(item.count) }}</Text>
                <Text variant="label-small" color="tertiary" class="label">{{ item.label }}</Text>
              </View>
            </div>
          </StatGroup>
        </StatsGrid>

        <PropertyDashboardMarketInsightsPreview @open="activeTab = StatsTab.MARKET_INSIGHTS" />
      </View>

      <View v-else direction="column" gap="lg">
        <View direction="column" gap="sm">
          <Text variant="h5">Comparable rentals</Text>
          <PropertyDashboardComparableRentals :limit="4" />
        </View>

        <View direction="column" gap="sm">
          <Text variant="h5">Price trend</Text>
          <PropertyDashboardPriceTrend />
        </View>
      </View>
    </TabTransition>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import {
  Card,
  IconButton,
  MenuButton,
  MenuButtonItem,
  screenMinMd,
  StatGroup,
  StatsGrid,
  Tabs,
  TabTransition,
  Text,
  View,
  useAlertsStore,
  type TabItem,
} from '@tallo/design-system';
import { AppRouteNames } from '@tallo/investor/navigation';
import { usePropertyStatisticsQuery, propertyStatisticsService, usePropertyStore } from '@tallo/investor/property';
import { TimeFilter, timeFilters, downloadFile, generateStatsFilename } from '@tallo/utility';

import PropertyDashboardComparableRentals from './property-dashboard-comparable-rentals.vue';
import PropertyDashboardMarketInsightsPreview from './property-dashboard-market-insights-preview.vue';
import PropertyDashboardPriceTrend from './property-dashboard-price-trend.vue';

type FunnelGroup = 'leads' | 'messaging' | 'showings' | 'applications';

interface StatGroupItem {
  key: FunnelGroup;
  title: string;
  to?: RouteLocationRaw;
  items: { label: string; count: number }[];
}

enum StatsTab {
  LEASING_FUNNEL = 'Leasing funnel',
  MARKET_INSIGHTS = 'Market insights',
}

const props = defineProps<{ propertyId: string }>();

const activeTab = ref(StatsTab.LEASING_FUNNEL);

const statsTabs: TabItem[] = [
  { key: StatsTab.LEASING_FUNNEL, label: 'Leasing funnel' },
  { key: StatsTab.MARKET_INSIGHTS, label: 'Market insights' },
];

const formatCount = (value: number) => value.toLocaleString('en-US');
// Two by two below the desktop width (the edge the grid and the labels below share): half a card per
// label, so the long one would wrap and push its numbers down
const isTwoByTwo = computed(() => !screenMinMd.value);

const { data, selectedFilter } = usePropertyStatisticsQuery();
const statistics = computed(() => data.value);
const alertsStore = useAlertsStore();
const propertyStore = usePropertyStore();

const propertyLeadsRoute = computed<RouteLocationRaw>(() => ({
  name: AppRouteNames.INVESTOR_PROPERTY_LEADS,
  params: {
    propertyId: props.propertyId,
  },
  query: {
    filter: 'All',
  },
}));

function onFilterSelected(filter: TimeFilter) {
  selectedFilter.value = filter;
}

async function handleDownloadStats() {
  try {
    const blob = await propertyStatisticsService.downloadPropertyStatistics(props.propertyId, selectedFilter.value);

    const propertyDisplayName = propertyStore.property?.displayName || `Property-${props.propertyId}`;
    const filename = generateStatsFilename(propertyDisplayName, selectedFilter.value);

    downloadFile(blob, filename);

    alertsStore.success('Statistics downloaded successfully');
  } catch (error) {
    console.error('Error downloading statistics:', error);
    alertsStore.error('Failed to download statistics. Please try again.');
  }
}

const funnelGroups = computed<StatGroupItem[]>(() => [
  {
    key: 'leads',
    title: 'Leads',
    to: propertyLeadsRoute.value,
    items: [
      { label: 'Total', count: statistics.value?.leads || 0 },
      { label: 'Qualified', count: statistics.value?.qualifiedLeads || 0 },
    ],
  },
  {
    key: 'messaging',
    title: isTwoByTwo.value ? 'Messaging' : 'Messaging activity',
    items: [
      { label: 'Messages sent', count: statistics.value?.messagesSent || 0 },
      { label: 'Follow-ups sent', count: statistics.value?.followUpsSent || 0 },
    ],
  },
  {
    key: 'showings',
    title: 'Showings',
    items: [
      { label: 'Total', count: statistics.value?.showingsTotal || 0 },
      { label: 'Booked', count: statistics.value?.showingsBooked || 0 },
    ],
  },
  {
    key: 'applications',
    title: 'Applications',
    items: [
      { label: 'Requested', count: statistics.value?.applicationsRequested || 0 },
      { label: 'Completed', count: statistics.value?.applicationsCompleted || 0 },
    ],
  },
]);

// Two by two, the groups with a "total" (Leads, Showings) share the first row
const mobileOrder: FunnelGroup[] = ['leads', 'showings', 'messaging', 'applications'];
const statCards = computed(() =>
  isTwoByTwo.value
    ? mobileOrder.map((key) => funnelGroups.value.find((group) => group.key === key)!)
    : funnelGroups.value,
);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.stats-title {
  width: 100%;
  row-gap: var(--t-spacing-sm);

  // A phone gets the tabs on their own line, the controls on the next
  @include mobile {
    flex-direction: column;
    align-items: stretch;
    gap: var(--t-spacing-md);
  }
}

// Time range on the left, download on the right, like the rest of the card's rows
.controls {
  @include mobile {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
}

// A ghost icon button's own inset would keep the glyph off the card's edge
.download {
  @include mobile {
    margin-right: -0.625rem;
  }
}

.controls-hidden {
  visibility: hidden;
  pointer-events: none;
}

// Figma: 24px gap between the card header (tabs) and the stats body (node 15639:11440).
#pd-performance :deep(.card-header) {
  margin-bottom: var(--t-spacing-lg);
}

.stat-items {
  display: flex;
  justify-content: start;
  gap: var(--t-spacing-xl);

  // Two by two, the two numbers of a group share their columns
  @include screen-max-md {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--t-spacing-sm);
  }
}
</style>
